import { useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '@/stores/auth'
import { studentsApi, type StudentResponse } from '@/api/endpoints/students'
import { classesApi } from '@/api/endpoints/classes'
import { parishesApi } from '@/api/endpoints/parishes'
import { printStudentList } from '@/utils/print'
import Spinner from '@/components/common/Spinner'
import ParentContactModal from '@/components/dashboard/modals/ParentContactModal'
import { useUI } from '@/stores/ui'
import { isAdminRole } from '@/utils/roles'

interface ClassOption { id: string; name: string }

export default function StudentsList({ open = false, onToggle }: { open?: boolean; onToggle?: () => void }) {
  const { user } = useAuth()
  const [students, setStudents] = useState<StudentResponse[]>([])
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [assignBusy, setAssignBusy] = useState<string | null>(null)
  const [parishName, setParishName] = useState('')
  const [contactFor, setContactFor] = useState<string | null>(null)
  const addToast = useUI((state) => state.addToast)
  // Spec: "Teachers cannot assign students - only Admins"
  const canAssign = isAdminRole(user?.role)

  const [classes, setClasses] = useState<ClassOption[]>([
    { id: 'none', name: 'NO REGISTRATION' },
  ])

  useEffect(() => {
    let mounted = true
    const run = async () => {
      if (!user?.parish_id) return
      setLoading(true)
      try {
        const [studentsData, classesData, parishesData] = await Promise.all([
          studentsApi.list(),
          classesApi.list(),
          parishesApi.list(),
        ])
        if (mounted) {
          setStudents(Array.isArray(studentsData) ? studentsData : [])
          // Add real classes to the list
          const classOptions: ClassOption[] = [
            { id: 'none', name: 'NO REGISTRATION' },
            ...(Array.isArray(classesData) ? classesData.map(c => ({ id: c.id, name: c.name })) : []),
          ]
          setClasses(classOptions)
          // Get parish name
          const activeParish = Array.isArray(parishesData) ? parishesData.find(p => p.id === user.parish_id) : null
          if (activeParish) setParishName(activeParish.name)
        }
      } finally {
        if (mounted) setLoading(false)
      }
    }
    run()
    return () => { mounted = false }
  }, [user?.parish_id])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return students
    return students.filter(s =>
      `${s.first_name} ${s.last_name} ${s.class_name || ''}`.toLowerCase().includes(q)
    )
  }, [students, query])

  const assign = async (s: StudentResponse, val: string) => {
    const newClassId = val === 'none' ? null : val
    const selectedClass = classes.find(c => c.id === val)
    setAssignBusy(s.id)
    const previous = students
    setStudents(prev => prev.map(p => p.id === s.id ? { ...p, class_id: newClassId, class_name: selectedClass?.name || null } : p))
    try {
      await studentsApi.assign(s.id, newClassId)
      addToast({ message: newClassId ? `${s.first_name} assigned to ${selectedClass?.name}` : `${s.first_name} unassigned`, type: 'success' })
    } catch (error: any) {
      setStudents(previous)
      addToast({ message: error.response?.data?.detail || 'Failed to assign student', type: 'error' })
    } finally {
      setAssignBusy(null)
    }
  }

  const renderRow = (s: StudentResponse) => {
    const dob = s.date_of_birth || s.dob
    const allergens = s.allergen_alerts ?? s.allergens ?? []
    const sacraments = s.sacraments_received ?? s.sacraments ?? []
    return (
      <div key={s.id} className="p-4 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="font-medium">{s.first_name} {s.last_name}</div>
          <div className="text-xs text-gray-600">
            {s.class_id ? <>Class: {s.class_name || '—'} · </> : null}
            DOB: {dob ? new Date(dob + 'T00:00:00').toLocaleDateString() : '—'}
            {s.is_parishioner === false ? <span className="text-amber-700"> · Not a parishioner</span> : null}
          </div>
          {sacraments.length > 0 && <div className="text-xs text-gray-500">Sacraments: {sacraments.join(', ')}</div>}
          {allergens.length > 0 && <div className="text-xs font-semibold text-red-600">Allergen alert: {allergens.join(', ')}</div>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button type="button" className="btn-secondary text-xs" onClick={() => setContactFor(s.id)} title="Show this child's parent and emergency contact details">
            Parent contact
          </button>
          {canAssign && (
            <select
              className="input text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
              disabled={assignBusy === s.id}
              value={s.class_id || 'none'}
              aria-label={`Class for ${s.first_name} ${s.last_name}`}
              title="Pick a class to assign this student (Admins only)"
              onChange={(e) => assign(s, e.target.value)}
            >
              {classes.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
        </div>
      </div>
    )
  }

  const unassigned = filtered.filter(s => !s.class_id)
  const assigned = filtered.filter(s => !!s.class_id)

  return (
    <motion.div
      className="mt-6 p-5 rounded-xl border border-gray-200 bg-white shadow-sm"
      aria-busy={loading}
      initial={{ y: 8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
    >
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={onToggle} aria-expanded={open} className="flex items-center gap-2 text-lg font-semibold text-gray-900 hover:text-gray-700 focus-ring rounded px-1 -ml-1">
          <span className={`text-gray-400 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}>▸</span>
          Students
        </button>
        <div className="flex items-center gap-2">
          <input
            className="input-field w-72 focus:outline-none focus:ring-2 focus:ring-yellow-400/60 border border-gray-300"
            placeholder="Search students…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search students"
          />
          <button
            className="btn-secondary text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
            onClick={() => {
              if (!parishName) {
                alert('Please wait for parish data to load')
                return
              }
              printStudentList({
                parishName,
                students: filtered,
                classes,
              })
            }}
            title="Print student list"
          >
            🖨️ Print
          </button>
        </div>
      </div>
      {open && (<>
      {loading && <Spinner label="Loading students…" />}
      {!loading && (
        <div className="space-y-6">
          <section>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-red-700">Unassigned</h4>
              <span className="text-xs px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200">{unassigned.length}</span>
            </div>
            <div className="divide-y divide-gray-100 rounded-lg border border-red-100">
              {unassigned.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">None</div>
              ) : unassigned.map(s => renderRow(s))}
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">Assigned</h4>
              <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 border border-gray-200">{assigned.length}</span>
            </div>
            <div className="divide-y divide-gray-100 rounded-lg border border-gray-100">
              {assigned.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">None</div>
              ) : assigned.map(s => renderRow(s))}
            </div>
          </section>
        </div>
      )}
      </>)}
      {contactFor && <ParentContactModal open={!!contactFor} onClose={() => setContactFor(null)} studentId={contactFor} />}
    </motion.div>
  )
}



